import { Formik } from "formik";
import {
  DisplayFormErrors,
  EfsMelcoEfsModelsCreateMapping,
  EfsMelcoEfsModelsFormat,
  EfsMelcoEfsModelsMapping,
  ProductionFulfillmentApi,
  useFormSubmit,
  VerticalForm,
  Drawer as DrawerType,
  DebugForm,
  useAuthenticatedAPIConfig,
} from "melco-shared-logic";
import { useQueryClient } from "react-query";
import {
  dataMappingToInitialValues,
  mergeAttributes,
  newDataMapping,
  toAPIOutputAttribute,
} from "../../helper/dataMappingHelper";
import { useDataMapping } from "../../hooks/inputFormats/useDataMapping";
import { ColorDataMapping } from "./ColorDataMapping";
import { LetteringDataMapping } from "./LetteringDataMapping";
import { PrintLayerDataMapping } from "./PrintLayerDataMapping";
import { SelectDataMappingType } from "./SelectDataMappingType";

type DataMappingFormMode = "add" | "edit";

type AddOrEditDataMappingDrawerProps = {
  drawer: DrawerType;
  inputFormat: EfsMelcoEfsModelsFormat;
  dataMappingId?: string;
  formMode: DataMappingFormMode;
};

export type DataMappingAttribute = {
  dataSource: string;
  colorCollection?: string;
  fontCollection?: string;
  sourceModifier?: string;
  value?: string;
};

export type DataMappingFormValueType = EfsMelcoEfsModelsMapping & {
  _color?: DataMappingAttribute;
  _text?: DataMappingAttribute;
  _font?: DataMappingAttribute;
  _display?: DataMappingAttribute;
};

export const AddOrEditDataMapping: React.FC<
  AddOrEditDataMappingDrawerProps
> = ({ drawer, inputFormat, dataMappingId, formMode }) => {
  let { data: dataMapping } = useDataMapping(dataMappingId);

  const queryClient = useQueryClient();

  const authenticatedApiConfig = useAuthenticatedAPIConfig();

  const { closeDrawer } = drawer;

  const invalidateDataMapping = (
    inputFormatId: string,
    dataMappingId: string | null | undefined
  ) => {
    queryClient.invalidateQueries(["dataMappings", inputFormatId]);

    if (dataMappingId) {
      queryClient.invalidateQueries(["dataMapping", dataMappingId]);
    }
  };

  const onSubmit = useFormSubmit<DataMappingFormValueType>(
    async (values: DataMappingFormValueType, { resetForm }) => {
      const { id, config_type, name, _color, _text, _font, _display } = values;

      let { outputs } = values;

      let payload: EfsMelcoEfsModelsCreateMapping | undefined = undefined;

      payload = {
        format_id: inputFormat.id!,
        name,
        global: true,
        config_type,
        outputs,
      };

      mergeAttributes(outputs, toAPIOutputAttribute("Color", _color));
      mergeAttributes(outputs, toAPIOutputAttribute("Text", _text));
      mergeAttributes(outputs, toAPIOutputAttribute("Font", _font));
      mergeAttributes(outputs, toAPIOutputAttribute("Display", _display));

      if (payload) {
        const api = new ProductionFulfillmentApi(authenticatedApiConfig());

        if (formMode === "add") {
          await api.productionFulfillmentMappingsCreate({
            efsMelcoEfsModelsCreateMapping: payload,
          });

          // reset form such that no old artefacts remain when the form is opened again
          resetForm({
            values: dataMappingToInitialValues(newDataMapping(inputFormat.id!)),
          });
        } else {
          const updatedDataMapping =
            await api.productionFulfillmentMappingsEdit({
              id: id!,
              efsMelcoEfsModelsCreateMapping: payload,
            });

          resetForm({
            values: dataMappingToInitialValues(updatedDataMapping),
          });
        }

        return { successMessages: [{ message: "default" }] };
      }
    },
    {
      translationPrefix: `dataMappings.actions.${
        formMode === "add" ? "add" : "edit"
      }`,

      onSuccess: ({ id }) => {
        invalidateDataMapping(inputFormat.id!, id);

        closeDrawer();
      },
      onError: ({ id }) => {
        invalidateDataMapping(inputFormat.id!, id);
      },
    }
  );

  if (!dataMapping && formMode === "edit") {
    return null;
  } else if (!dataMapping && formMode === "add") {
    dataMapping = newDataMapping(inputFormat.id!);
  }

  const initialValues = dataMappingToInitialValues(dataMapping!);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, helpers) =>
        onSubmit(values, helpers, { isManuallyResettingForm: true })
      }
      validateOnBlur={false}
      validateOnChange={false}
    >
      <VerticalForm>
        <DisplayFormErrors />

        <SelectDataMappingType isDisabled={formMode === "edit"} />

        <ColorDataMapping inputFormat={inputFormat} />
        <LetteringDataMapping inputFormat={inputFormat} />
        <PrintLayerDataMapping inputFormat={inputFormat} />

        {false && <DebugForm />}
      </VerticalForm>
    </Formik>
  );
};
